// QuestionEscala - Pregunta tipo Escala 1-5
// Sprint 3 - Mobile-first design

import React, { useState } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Chip,
  Rating,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { PreguntaCompleta } from '../../../../types/formulario.types';
import { QuestionResponse } from '../../../../types/audit.types';

interface QuestionEscalaProps {
  pregunta: PreguntaCompleta;
  respuesta?: QuestionResponse;
  onChange: (valor: any, comentario?: string) => void;
}

const QuestionEscala: React.FC<QuestionEscalaProps> = ({
  pregunta,
  respuesta,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const valorActual = (respuesta as any)?.response_value as { valor?: number } | undefined;
  const [comentario, setComentario] = useState((respuesta as any)?.comment || '');

  const handleRespuestaChange = (_event: React.SyntheticEvent, newValue: number | null) => {
    if (newValue) {
      onChange({ valor: newValue }, comentario || undefined);
    }
  };

  const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue) {
      const valor = parseInt(newValue, 10);
      onChange({ valor }, comentario || undefined);
    }
  };

  const handleComentarioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nuevoComentario = event.target.value;
    setComentario(nuevoComentario);
    if (valorActual?.valor) {
      onChange({ valor: valorActual.valor }, nuevoComentario || undefined);
    }
  };

  const getColorForValue = (value: number): string => {
    if (value <= 2) return '#d32f2f'; // error.main
    if (value === 3) return '#ed6c02'; // warning.main
    return '#2e7d32'; // success.main
  };

  return (
    <Box>
      {/* Header de la pregunta */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Box
            sx={{
              fontSize: { xs: '0.9rem', sm: '1.0625rem' },
              fontWeight: 600,
              color: 'text.primary',
              flex: 1,
              lineHeight: 1.4,
            }}
          >
            {(pregunta as any).text}
          </Box>
          {(pregunta as any).required && (
            <Chip
              label="Obligatoria"
              size="small"
              color="error"
              sx={{ 
                height: { xs: 18, sm: 20 }, 
                fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                flexShrink: 0
              }}
            />
          )}
        </Box>
        
        {(pregunta as any).description && (
          <Box
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              color: 'text.secondary',
              fontStyle: 'italic',
              lineHeight: 1.3,
            }}
          >
            {(pregunta as any).description}
          </Box>
        )}
      </Box>

      {/* Rating con estrellas (Desktop) */}
      {!isMobile && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Rating
            value={valorActual?.valor || null}
            onChange={handleRespuestaChange}
            size="large"
            icon={<StarIcon fontSize="inherit" />}
            emptyIcon={<StarBorderIcon fontSize="inherit" />}
            sx={{
              fontSize: '2.5rem',
              '& .MuiRating-iconFilled': {
                color: valorActual?.valor ? getColorForValue(valorActual.valor) : 'action.active',
              },
            }}
          />
          {valorActual?.valor && (
            <Chip
              label={`${valorActual.valor} de 5`}
              sx={{
                bgcolor: getColorForValue(valorActual.valor),
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      )}

      {/* Botones 1-5 (Mobile) - Touch-friendly */}
      {isMobile && (
        <ToggleButtonGroup
          value={valorActual?.valor?.toString() || null}
          exclusive
          onChange={handleToggleChange}
          fullWidth
          sx={{
            mb: 1.5,
            gap: 0.75,
            '& .MuiToggleButton-root': {
              py: 2,
              px: 1,
              fontSize: '1.25rem',
              fontWeight: 700,
              borderWidth: 2,
              borderRadius: 1.5,
              minHeight: 52,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                borderWidth: 3,
                color: '#fff !important',
                transform: 'scale(1.05)',
                boxShadow: 2,
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '&.Mui-selected[value="1"]': {
                backgroundColor: '#d32f2f !important',
                borderColor: '#c62828 !important',
                '&:hover': {
                  backgroundColor: '#c62828 !important',
                  filter: 'brightness(0.9)',
                },
              },
              '&.Mui-selected[value="2"]': {
                backgroundColor: '#d32f2f !important',
                borderColor: '#c62828 !important',
                '&:hover': {
                  backgroundColor: '#c62828 !important',
                  filter: 'brightness(0.9)',
                },
              },
              '&.Mui-selected[value="3"]': {
                backgroundColor: '#ed6c02 !important',
                borderColor: '#e65100 !important',
                '&:hover': {
                  backgroundColor: '#e65100 !important',
                  filter: 'brightness(0.9)',
                },
              },
              '&.Mui-selected[value="4"]': {
                backgroundColor: '#2e7d32 !important',
                borderColor: '#1b5e20 !important',
                '&:hover': {
                  backgroundColor: '#1b5e20 !important',
                  filter: 'brightness(0.9)',
                },
              },
              '&.Mui-selected[value="5"]': {
                backgroundColor: '#2e7d32 !important',
                borderColor: '#1b5e20 !important',
                '&:hover': {
                  backgroundColor: '#1b5e20 !important',
                  filter: 'brightness(0.9)',
                },
              },
            },
          }}
        >
          {[1, 2, 3, 4, 5].map((valor) => (
            <ToggleButton
              key={valor}
              value={valor.toString()}
            >
              {valor}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {/* Leyenda */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: 'text.secondary',
          mb: pregunta.permite_comentario ? 2 : 0,
        }}
      >
        <span>1 - Muy malo</span>
        <span>3 - Regular</span>
        <span>5 - Excelente</span>
      </Box>

      {/* Campo de comentario (opcional) */}
      {pregunta.permite_comentario && (
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Comentario (opcional)"
          value={comentario}
          onChange={handleComentarioChange}
          placeholder="Agrega un comentario sobre esta calificación..."
          variant="outlined"
          size="small"
          name={`comentario-${pregunta.id}`}
          id={`comentario-escala-${pregunta.id}`}
          inputProps={{
            name: `comentario-${pregunta.id}`,
          }}
          InputProps={{
            name: `comentario-${pregunta.id}`,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
            },
          }}
        />
      )}
    </Box>
  );
};

export default QuestionEscala;
